import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VehicleBrowser from '../components/VehicleBrowser';
import VINDecoder from '../components/VINDecoder';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Car, Hash, Search } from 'lucide-react';

const VehicleSelectorPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  const handleVehicleSelected = (vehicle) => {
    setSelectedVehicle(vehicle);
    // Persist full selection for product detail badge
    localStorage.setItem('fitmentSelection', JSON.stringify(vehicle));

    // Build full fitment query
    const params = new URLSearchParams({
      vehicle_region: vehicle.region,
      vehicle_year: String(vehicle.year),
      vehicle_make: vehicle.make,
      vehicle_model: vehicle.model,
      vehicle_trim: vehicle.trim,
      vehicle_engine: vehicle.engine,
    });

    setTimeout(() => {
      navigate(`/products?${params.toString()}`);
    }, 200);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <div className="bg-blue-100 p-4 rounded-full">
              <Search className="h-12 w-12 text-blue-600" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {t('home.find_parts')}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t('home.select_details')}
          </p>
        </div>

        {/* Vehicle Selection Tabs */}
        <Tabs defaultValue="browse" className="w-full">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-8">
            <TabsTrigger value="browse" className="flex items-center gap-2">
              <Car className="h-4 w-4" />
              {t('vehicle.browse_by')}
            </TabsTrigger>
            <TabsTrigger value="vin" className="flex items-center gap-2">
              <Hash className="h-4 w-4" />
              {t('vin.decoder')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="browse" className="space-y-6">
            <VehicleBrowser onVehicleSelected={handleVehicleSelected} />
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-lg mb-3">{t('vehicle.why_browse_title')}</h3>
              <ul className="space-y-2 text-gray-700">
                {t('vehicle.why_browse_points', { returnObjects: true }).map((point, idx) => (
                  <li className="flex items-start gap-2" key={idx}>
                    <span className="text-blue-600 font-bold">✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>

          <TabsContent value="vin" className="space-y-6">
            <VINDecoder 
              onVehicleDecoded={handleVehicleSelected}
              showFullInfo={true}
            />
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h3 className="font-semibold text-lg mb-3">{t('vehicle.why_vin_title')}</h3>
              <ul className="space-y-2 text-gray-700">
                {t('vehicle.why_vin_points', { returnObjects: true }).map((point, idx) => (
                  <li className="flex items-start gap-2" key={idx}>
                    <span className="text-orange-600 font-bold">✓</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </TabsContent>
        </Tabs>

        {/* Popular Vehicles Section */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
            {t('vehicle.popular_vehicles')}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { make: 'FORD', model: 'F-150', year: 2020 },
              { make: 'TOYOTA', model: 'Camry', year: 2021 },
              { make: 'HONDA', model: 'Accord', year: 2020 },
              { make: 'CHEVROLET', model: 'Silverado', year: 2021 },
            ].map((vehicle, index) => (
              <button
                key={index}
                onClick={() => handleVehicleSelected(vehicle)}
                className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all text-left"
              >
                <div className="font-semibold text-gray-900">{vehicle.make}</div>
                <div className="text-sm text-gray-600">{vehicle.model}</div>
                <div className="text-xs text-gray-500 mt-1">{vehicle.year}</div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VehicleSelectorPage;
